import {
  Goal,
  goalsArchivedSectionID,
  ID,
  rootId,
  TaskListItemTypes,
  TaskListTreeItem,
} from '@griply/types';

import {
  getArchivedGoalIds,
  getCompletedGoalIds,
} from '../../derived-goal-state';
import { getNearestPresentParentId } from '../get-nearest-present-parent-id';
import { BuildGoalListTreeOptions } from './types';

export const buildNoGroupingGoalListTree = (
  goals: Goal[],
  {
    hideArchived,
    hideCompleted,
    archivedSectionName,
    allGoals,
  }: BuildGoalListTreeOptions,
): Record<ID, TaskListTreeItem> => {
  const archivedIds = getArchivedGoalIds(allGoals);
  const completedIds = getCompletedGoalIds(allGoals);
  const activeGoals: ID[] = [];
  const archivedGoals: ID[] = [];

  const goalMap = goals.reduce<Record<ID, Goal>>((acc, goal) => {
    acc[goal.id] = goal;
    return acc;
  }, {});

  const goalChildrenMap = goals.reduce<Record<ID, ID[]>>((acc, goal) => {
    acc[goal.id] = acc[goal.id] ?? [];
    const directParentId = goal.parentIds?.[goal.parentIds.length - 1];
    if (directParentId && goalMap[directParentId]) {
      acc[directParentId] = [...(acc[directParentId] ?? []), goal.id];
    }
    return acc;
  }, {});

  const treeItems: Record<ID, TaskListTreeItem> = {};

  goals.forEach((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return;
    }
    if (!hideArchived && goal.archivedAt) {
      archivedGoals.push(goal.id);
    }

    if (!hideCompleted || !completedIds.has(goal.id)) {
      activeGoals.push(goal.id);
    }

    const children =
      goalChildrenMap[goal.id]?.filter((id) => {
        const child = goalMap[id];
        return (
          child &&
          !child.archivedAt &&
          (!hideCompleted || !completedIds.has(id))
        );
      }) ?? [];
    treeItems[goal.id] = {
      id: goal.id,
      name: goal.name,
      type: TaskListItemTypes.Goal,
      children,
      isFolder: !!children.length,
    };
  });

  // Archived through an ancestor the filter dropped — no parent left to nest under.
  activeGoals.forEach((goalId) => {
    const goal = goalMap[goalId];
    if (goal.archivedAt || !archivedIds.has(goalId)) {
      return;
    }
    if (!getNearestPresentParentId(goal, (id) => !!goalMap[id])) {
      archivedGoals.push(goalId);
    }
  });

  if (archivedGoals.length) {
    treeItems[goalsArchivedSectionID] = {
      id: goalsArchivedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: archivedSectionName,
      children: archivedGoals,
      isFolder: true,
    };
  }

  const rootChildren = activeGoals.filter((goalId) => {
    if (!hideArchived && archivedIds.has(goalId)) {
      return false;
    }
    const goal = goalMap[goalId];
    return !getNearestPresentParentId(goal, (id) => !!treeItems[id]);
  });

  if (archivedGoals.length) {
    rootChildren.push(goalsArchivedSectionID);
  }

  return {
    [rootId]: {
      id: rootId,
      name: 'Root',
      type: TaskListItemTypes.Root,
      children: rootChildren,
    },
    ...treeItems,
  };
};
